import React, { useEffect, useRef, useState } from 'react';
import { getProfile, updateProfile } from '../api';
import ImageUploadField from '../components/ImageUploadField';
import { DEFAULT_LANYARD, getLanyardContent } from '../lanyardDefaults';
import './AdminLanyard.css';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export default function AdminLanyard() {
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_LANYARD);
  const [side, setSide] = useState('front');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const stageRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    getProfile()
      .then((response) => {
        setProfile(response.data);
        setDraft(getLanyardContent(response.data.lanyard));
      })
      .catch(() => setMessage('读取吊牌配置失败，请刷新页面重试。'));
  }, []);

  const updateDraft = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setMessage('');
  };

  const handlePointerDown = (event) => {
    if (!draft.visible || !stageRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      horizontal: draft.horizontal,
      vertical: draft.vertical,
      width: stageRef.current.clientWidth,
      height: stageRef.current.clientHeight,
    };
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    const drag = dragRef.current;
    setDraft((current) => ({
      ...current,
      horizontal: Math.round(clamp(drag.horizontal + (event.clientX - drag.x) / drag.width * 100, 15, 85)),
      vertical: Math.round(clamp(drag.vertical + (event.clientY - drag.y) / drag.height * 800, -80, 80)),
    }));
  };

  const handlePointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!profile) return;
    setSaving(true);
    setMessage('');
    try {
      const nextProfile = { ...profile, lanyard: draft };
      await updateProfile(nextProfile);
      setProfile(nextProfile);
      setMessage('吊牌已保存，首页刷新后生效。');
    } catch (error) {
      setMessage(error?.response?.data?.error || '保存失败，请重试。');
    } finally {
      setSaving(false);
    }
  };

  if (!profile && !message) return <div className="p-8 text-white">加载吊牌配置...</div>;

  return (
    <main className="lanyard-editor p-6 lg:p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.28em] text-blue-300">Hanging card studio</p>
          <h2 className="text-3xl font-bold text-white">吊牌编辑</h2>
          <p className="mt-3 text-sm text-gray-400">在左侧预览位置，右侧编辑内容。当前吊牌只在桌面版首页显示。</p>
        </div>
        <span className="rounded-full border border-gray-700 px-3 py-1 text-xs text-gray-300">首屏组件</span>
      </div>

      {message && <div role="status" className="mb-6 rounded-xl border border-blue-500/40 bg-blue-950/40 px-4 py-3 text-sm text-blue-100">{message}</div>}

      {profile && <form onSubmit={handleSave} className="lanyard-editor-grid">
        <section className="lanyard-preview-panel" aria-label="吊牌位置预览">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-white">首屏预览</h3>
              <p className="mt-1 text-xs text-gray-400">拖动吊牌调整位置。预览为示意图。</p>
            </div>
            <div className="flex rounded-lg border border-gray-700 bg-dark p-1" aria-label="预览吊牌面">
              {['front', 'back'].map((item) => (
                <button key={item} type="button" onClick={() => setSide(item)}
                  className={`rounded-md px-4 py-1.5 text-sm transition ${side === item ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-white'}`}>
                  {item === 'front' ? '正面' : '背面'}
                </button>
              ))}
            </div>
          </div>

          <div ref={stageRef} className="lanyard-preview-stage">
            <div className="lanyard-preview-banner" style={{ backgroundImage: `url(${profile.bannerImage || '/image.png'})` }} />
            <div className="lanyard-preview-copy">
              <strong>嗨！我是 {profile.name}</strong>
              <span>{profile.title}</span>
              <i />
              <i />
              <i />
            </div>
            <img className="lanyard-preview-portrait" src={profile.image || '/形象照1.png'} alt="个人展示图"
              onError={(event) => { event.currentTarget.src = '/形象照1.png'; }} />
            <div className="lanyard-preview-rope" style={{ left: `calc(${draft.horizontal}% + 16px)`, height: `calc(30% + ${draft.vertical / 8}%)`, opacity: draft.visible ? 1 : 0.3 }} />
            <div className={`lanyard-preview-card ${draft.visible ? '' : 'is-hidden'}`}
              style={{ left: `calc(${draft.horizontal}% + 16px)`, top: `calc(52% + ${draft.vertical / 8}%)` }}
              onPointerDown={handlePointerDown} onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}
              role="img" aria-label="拖动吊牌调整位置">
              <span className="lanyard-preview-hole" />
              {side === 'front' ? <>
                {draft.icon && <img src={draft.icon} alt="" />}
                <span className="lanyard-preview-card-text">{draft.frontText}</span>
              </> : <span className="lanyard-preview-card-text back">{draft.backText}</span>}
              {!draft.visible && <span className="lanyard-preview-hidden-label">已隐藏</span>}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-gray-400">
            <span>水平位置：{draft.horizontal}%</span>
            <span className="text-right">上下调节：{draft.vertical > 0 ? '+' : ''}{draft.vertical}</span>
          </div>
        </section>

        <div className="space-y-5">
          <section className="lanyard-control-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-white">显示吊牌</h3>
                <p className="mt-1 text-xs text-gray-400">关闭后首页隐藏整个吊牌和挂绳。</p>
              </div>
              <button type="button" role="switch" aria-checked={draft.visible}
                aria-label="显示吊牌" onClick={() => updateDraft('visible', !draft.visible)}
                className={`lanyard-visibility-switch ${draft.visible ? 'is-on' : ''}`}>
                <span />
              </button>
            </div>
          </section>

          <section className="lanyard-control-panel space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-white">位置</h3>
              <button type="button" onClick={() => setDraft((current) => ({ ...current, horizontal: 50, vertical: 0 }))}
                className="text-xs font-semibold text-blue-300 hover:text-white">恢复默认位置</button>
            </div>
            <div>
              <label htmlFor="lanyard-horizontal" className="mb-2 flex justify-between text-sm text-gray-300"><span>左右位置</span><span>{draft.horizontal}%</span></label>
              <input id="lanyard-horizontal" type="range" min="15" max="85" value={draft.horizontal}
                onChange={(event) => updateDraft('horizontal', Number(event.target.value))} className="w-full accent-blue-500" />
            </div>
            <div>
              <label htmlFor="lanyard-vertical" className="mb-2 flex justify-between text-sm text-gray-300"><span>上下位置</span><span>{draft.vertical > 0 ? '+' : ''}{draft.vertical}</span></label>
              <input id="lanyard-vertical" type="range" min="-80" max="80" value={draft.vertical}
                onChange={(event) => updateDraft('vertical', Number(event.target.value))} className="w-full accent-blue-500" />
              <p className="mt-2 text-xs text-gray-500">上下调节会改变挂绳长度，顶部挂点保持原位。</p>
            </div>
          </section>

          <section className="lanyard-control-panel space-y-5">
            <h3 className="font-semibold text-white">吊牌内容</h3>
            <div>
              <label htmlFor="lanyard-front-text" className="mb-2 block text-sm text-gray-300">正面提示文字</label>
              <input id="lanyard-front-text" type="text" maxLength={120} value={draft.frontText}
                onChange={(event) => updateDraft('frontText', event.target.value)} className="lanyard-editor-input" />
            </div>
            <div>
              <label htmlFor="lanyard-back-text" className="mb-2 block text-sm text-gray-300">背面文字</label>
              <textarea id="lanyard-back-text" rows={4} maxLength={400} value={draft.backText}
                onChange={(event) => updateDraft('backText', event.target.value)} className="lanyard-editor-input" />
              <p className="mt-2 text-xs text-gray-500">换行会保留。文字过长可能超出吊牌，请查看预览。</p>
            </div>
            <ImageUploadField label="正面图标" value={draft.icon}
              onChange={(imageUrl) => updateDraft('icon', imageUrl)} folder="lanyard" allowRelativeUrl
              description="可上传图片，也可粘贴图片地址。清空后隐藏图标。" />
          </section>

          <button type="submit" disabled={saving} className="w-full rounded-xl bg-blue-600 px-5 py-4 font-semibold text-white transition hover:bg-blue-500 disabled:opacity-60">
            {saving ? '保存中...' : '保存吊牌设置'}
          </button>
        </div>
      </form>}
    </main>
  );
}
